import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, message } from 'antd';
import { ArrowRightOutlined, FileTextOutlined } from '@ant-design/icons';
import { financeSettingsApi } from '../../utils/api';
import EstimateForm from '../../components/finance/EstimateForm';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { mapItemDiscountFields } from '../../utils/itemDiscountUtils';

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

const EditEstimate = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [options, setOptions] = useState({
    currencies: [],
    clients: [],
    bankAccounts: [],
    categories: [],
    products: [],
    units: [],
    taxes: [],
  });
  const [initialValues, setInitialValues] = useState(null);
  const [initialItems, setInitialItems] = useState([]);

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/estimates', { state: buildGoBackState() });
  };

  useEffect(() => {
    const fetch = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await financeSettingsApi.getEstimateEditData(id);
        const data = res?.data || {};
        const estimate = data.estimate || {};
        setOptions({
          currencies: data.currencies || [],
          clients: data.clients || [],
          bankAccounts: data.bankAccounts || [],
          categories: data.categories || [],
          products: data.products || [],
          units: data.units || [],
          taxes: data.taxes || [],
        });
        const mappedItems = (estimate.items || []).map((it) => {
          let parsedTaxes = [];
          try {
            parsedTaxes = Array.isArray(it.taxes) ? it.taxes : JSON.parse(it.taxes || '[]');
          } catch {
            parsedTaxes = [];
          }
          return {
            item_id: it.id,
            product_id: it.product_id,
            item_name: it.item_name || '',
            quantity: Number(it.quantity || 0),
            unit_id: it.unit_id,
            cost_per_item: Number(it.unit_price || 0),
            taxes: parsedTaxes.map((x) => Number(x)),
            item_summary: it.item_summary || '',
            ...mapItemDiscountFields(it),
          };
        });
        setInitialItems(
          mappedItems.length
            ? mappedItems
            : [
                {
                  item_id: null,
                  product_id: null,
                  item_name: '',
                  quantity: 1,
                  unit_id: null,
                  cost_per_item: 0,
                  discount: 0,
                  discount_type: 'percent',
                  taxes: [],
                  amount: 0,
                  item_summary: '',
                },
              ],
        );        setInitialValues({
          estimate_number: estimate.estimate_number,
          valid_till: formatJalaliApiDate(estimate.valid_till),
          currency_id: estimate.currency_id,
          client_id: estimate.client_id,
          bank_account_id: estimate.bank_account_id,
          category_id: estimate.category_id || undefined,
          calculate_tax: estimate.calculate_tax || 'after_discount',
          discount_value: Number(estimate.discount || 0),
          discount_type: estimate.discount_type || 'percent',
          description: stripHtml(estimate.description),
          note: estimate.note || '',
          status: estimate.status || 'waiting',
        });
      } catch (error) {
        console.error('Error fetching estimate edit data:', error);
        message.error('خطا در دریافت اطلاعات پیش‌فاکتور');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const handleSubmit = async (payload) => {
    setSubmitLoading(true);
    try {
      await financeSettingsApi.updateEstimate(id, payload);
      message.success('پیش‌فاکتور با موفقیت ویرایش شد');
      navigate(returnToRef.current || '/finance/estimates', { state: buildGoBackState() });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <FileTextOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش پیش‌فاکتور</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ویرایش پیش‌فاکتور شماره {id}</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={goBackToList} style={{ fontFamily: 'CustomFont' }}>
              بازگشت به لیست
            </Button>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={6} columns={2} />
            ) : (
              <EstimateForm
                options={options}
                initialValues={initialValues}
                initialItems={initialItems}
                isEdit={true}
                onSubmit={handleSubmit}
                submitLoading={submitLoading}
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditEstimate;
